import React, { useEffect, useState } from 'react';
import { Select } from 'antd';
import { Resolver } from 'react-hook-form';
import {
    Controller,
    Input,
    useForm,
    yupResolver,
    SubmitHandler,
    useGetApiRequests,
    useSelector,
    ErrorMessages,
    ErrorMessage
} from '../../../common/CommonExport';
import { OrganisationIdState } from '../../../common/types';
import { DesignationProps, DesignationTableRecord } from '../OrganisationsInterfaces';
import { CreateDesignationSchema } from '../Department/CreateDepartmentSchema';
import DesignationEditor from './DesignationEditor/DesignationEditor';
import FloatLabel from '../../../components/FloatLabel';
import ModalLoader from '../../../components/Loader/ModalLoader';
import PopupModal from '../../employee/employee-details/employee-profile/modal/PopupModal';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    DepartmentListItem,
    GradeListItem,
    DepartmentApiResponse,
    GradeApiResponse,
    DesignationApiParams,
    ApiErrorResponse,
    EditPathParams
} from './CreateDesignationModalTypes';
import { DesignationListApiResponse, DesignationListParams } from './types';
import KPISection from './KPISection';

const CreateDesignationPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const locationState = location.state as { editRecord?: DesignationTableRecord; viewOnly?: boolean } | undefined;
    const isEdit = Boolean(id);
    const isViewOnly = Boolean(locationState?.viewOnly);

    const designationDepartmentApi = useGetApiRequests('designationDepartmentList', 'GET');
    const designationGradeApi = useGetApiRequests('designationGrade', 'GET');
    const designationCreate = useGetApiRequests('createDesignation', 'POST');
    const designationEdit = useGetApiRequests('editDesignation', 'PUT');
    const designationListApi = useGetApiRequests('designation', 'GET');

    const [loader, setLoader] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const orgId = useSelector((state: OrganisationIdState) => state?.organisationId?.id);

    const [designationDepartmentList, setDesignationDepartmentList] = useState<DepartmentListItem[]>([]);
    const [gradeList, setGradeList] = useState<GradeListItem[]>([]);
    const [editRecord, setEditRecord] = useState<DesignationTableRecord | Record<string, never>>(locationState?.editRecord ?? {});
    const [editId, setEditId] = useState<number | undefined>(locationState?.editRecord?.departmentId);
    const [editGrade, setEditGrade] = useState<number | undefined>(locationState?.editRecord?.gradeId);
    const [validateDept, setvalidateDept] = useState<boolean>(true);
    const [validateGrade, setvalidateGrade] = useState<boolean>(true);
    const [modalerrMessage, setModalErrMessage] = useState<string>('');
    const [popModal, setPopModal] = useState<boolean>(false);
    const [modalResponse, setModalResponse] = useState<string>('');
    const [modalButtonShow, setmodalButtonShow] = useState<boolean>(true);
    const [createGrade, setCreateGrade] = useState<number | string>('');
    const [createDepartment, setCreateDepartment] = useState<number | string>('');

    const {
        handleSubmit,
        control,
        reset,
        setValue,
        formState: { errors },
    } = useForm<DesignationProps>({
        resolver: yupResolver(CreateDesignationSchema) as Resolver<DesignationProps>,
        defaultValues: { department: '', designation: '', grade: '', roles: '', experience: '', compensation: '', kpiQuestionaires: [] }
    });

    const renderRolesAndResponsibilites = (values: string): string => {
        const decodeHtmlEntities = (str: string): string => {
            return str.replace(/&#(\d+);/g, (_match: string, dec: string) => {
                return String.fromCharCode(parseInt(dec));
            });
        };

        let finalOutputArray: string[] = [];
        let finalOutput = values.split('</p></li>');
        finalOutput.forEach((item: string) => {
            let decodedItem = decodeHtmlEntities(item);
            let withoutHtmlTags = decodedItem.replace(/<[^>]*>/g, '');
            if (withoutHtmlTags.trim() !== '') {
                finalOutputArray.push(withoutHtmlTags.trim());
            }
        });

        return finalOutputArray.join('; ');
    };

    const applyEditRecord = (record: DesignationTableRecord) => {
        setEditId(record.departmentId);
        setEditGrade(record.gradeId);
        setValue('department', record.departmentId?.toString() || '');
        setValue('grade', record.gradeId?.toString() || '');
        setValue('designation', record.designation);
        setValue('roles', record.rolesAndResponsibilities);
        setValue('experience', record.experience);
        setValue('compensation', record.compensation);
        setValue('kpiQuestionaires', record.kpiQuestionaires || []);
    };

    useEffect(() => {
        departmentGetListApi();
        gradeListGetApi();
        if (!isEdit) {
            reset({ department: '', designation: '', grade: '', roles: '', experience: '', compensation: '', kpiQuestionaires: [] });
        }
    }, []);

    useEffect(() => {
        if (!isEdit) return;
        const record = editRecord as DesignationTableRecord;
        if (record && Object.keys(record).length > 0) {
            applyEditRecord(record);
        }
    }, [isEdit, editRecord]);

    useEffect(() => {
        if (!isEdit || locationState?.editRecord || !id) return;
        if (!orgId) return;
        fetchDesignationById(Number(id));
    }, [isEdit, id, orgId, locationState?.editRecord]);

    const departmentGetListApi = () => {
        const params: DesignationApiParams = {
            organisationId: orgId,
        };
        setLoader(true);
        designationDepartmentApi('', params)
            .then((response) => {
                const res = response as DepartmentApiResponse;
                const reformedDepartmentList: DepartmentListItem[] = res?.data?.data.map((item) => ({
                    value: item.departmentId,
                    label: item.name,
                    designation: item.designation,
                    compensation: item?.compensation || "",
                    experience: item?.experience || "",
                }));
                setDesignationDepartmentList(reformedDepartmentList);
                setLoader(false);
            })
            .catch((_err: ApiErrorResponse) => {
                setPopModal(true);
                setmodalButtonShow(false);
                setModalErrMessage(ErrorMessages.somethingWentWrong);
                setModalResponse('error');
                setTimeout(() => {
                    handleClosePopUpModal();
                    setLoader(false);
                }, 1500);
            });
    };

    const gradeListGetApi = () => {
        setLoader(true);
        designationGradeApi('')
            .then((response) => {
                const res = response as GradeApiResponse;
                const reformedGradeList: GradeListItem[] = res?.data?.data.map((item) => ({
                    value: item?.cntGradeId,
                    label: item.name,
                    tier: item.tier,
                    cntGradeId: item?.cntGradeId,
                    name: item.name,
                }));
                setGradeList(reformedGradeList);
                setLoader(false);
            })
            .catch((_err: ApiErrorResponse) => {
                setPopModal(true);
                setmodalButtonShow(false);
                setModalErrMessage(ErrorMessages.somethingWentWrong);
                setModalResponse('error');
                setTimeout(() => {
                    handleClosePopUpModal();
                    setLoader(false);
                }, 1500);
            });
    };

    const fetchDesignationById = (designationId: number) => {
        const params: DesignationListParams = {
            start: 0,
            limit: 1000,
            organisationId: orgId,
            isActive: true,
        };
        setLoader(true);
        designationListApi('', params)
            .then((response) => {
                const apiResponse = response as DesignationListApiResponse;
                const match = apiResponse?.data?.data?.find((item) => item?.designationId === designationId);
                if (!match) {
                    setPopModal(true);
                    setmodalButtonShow(false);
                    setModalErrMessage('Designation not found.');
                    setModalResponse('error');
                    setTimeout(() => {
                        handleClosePopUpModal();
                        handleClosePage();
                    }, 1500);
                    return;
                }
                const mappedRecord: DesignationTableRecord = {
                    designation: match?.name,
                    department: match?.department?.name,
                    departmentId: match?.department?.departmentId,
                    grade: match?.grade?.name,
                    gradeId: match?.grade?.cntGradeId,
                    rolesAndResponsibilities: match?.rolesAndResponsiblities && renderRolesAndResponsibilites(match?.rolesAndResponsiblities),
                    key: match?.designationId,
                    quilldata: match?.rolesAndResponsiblities,
                    experience: match?.experience,
                    compensation: match?.compensation,
                    kpiQuestionaires: match?.kpiQuestionaires,
                };
                setEditRecord(mappedRecord);
                setLoader(false);
            })
            .catch((_err: ApiErrorResponse) => {
                setPopModal(true);
                setmodalButtonShow(false);
                setModalErrMessage(ErrorMessages.somethingWentWrong);
                setModalResponse('error');
                setTimeout(() => {
                    handleClosePopUpModal();
                    setLoader(false);
                }, 1500);
            });
    };

    const onChange = (item: number) => {
        if (isEdit) {
            setvalidateDept(false);
            setEditId(item);
            setValue('department', item.toString());
        } else {
            setvalidateDept(false);
            const gradeTempList = designationDepartmentList.find((e) => e.value === item);
            setCreateDepartment(gradeTempList?.value || '');
            setValue('department', item.toString());
        }
    };

    const gradeOnChange = (value: number) => {
        if (isEdit) {
            setvalidateGrade(false);
            setEditGrade(value);
            setValue('grade', value.toString());
        } else {
            const gradeTempList = gradeList.find((e) => e.value === value);
            setCreateGrade(gradeTempList?.value || '');
            setValue('grade', value.toString());
            setvalidateGrade(false);
        }
    };

    const handleClosePopUpModal = () => {
        setPopModal(false);
    };

    const handleClosePage = () => {
        reset({ department: '', designation: '', grade: '', roles: '', experience: '', compensation: '', kpiQuestionaires: [] });
        navigate('/home/organisation', { state: { activeTab: '2' } });
    };

    const filterOption = (input: string, option?: { label: string; value: string | number }) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase());

    const handleApiCall = async (payload: object, pathParams: object, successMessage: string) => {
        setSubmitLoading(true);
        try {
            await (isEdit ? designationEdit(payload, {}, pathParams) : designationCreate(payload));
            setSubmitLoading(false);
            setPopModal(true);
            setmodalButtonShow(false);
            setModalErrMessage(successMessage);
            setModalResponse('success');
            setTimeout(() => {
                handleClosePopUpModal();
                handleClosePage();
            }, 1500);
        } catch (err) {
            const error = err as ApiErrorResponse;
            setPopModal(true);
            setmodalButtonShow(false);
            setModalErrMessage(error?.response?.data?.message || ErrorMessages.somethingWentWrong);
            setModalResponse('error');
            setTimeout(() => {
                handleClosePopUpModal();
                setSubmitLoading(false);
            }, 1500);
        }
    };

    const onSubmit: SubmitHandler<DesignationProps> = (data: DesignationProps) => {
        if (isViewOnly) {
            return;
        }
        const gradeObject = gradeList.find((item) => {
            if (item.value === parseInt(data.grade)) {
                return item;
            }
        });

        const payload = {
            name: data.designation,
            department: {
                departmentId: data.department,
            },
            grade: gradeObject,
            organisationId: orgId,
            rolesAndResponsiblities: data?.roles,
            experience: data?.experience,
            compensation: data?.compensation,
            kpiQuestionaires: data.kpiQuestionaires,
        };
        if (isEdit) {
            const editIdParam = id ? Number(id) : (editRecord as DesignationTableRecord)?.key;
            if (!editIdParam) {
                setPopModal(true);
                setmodalButtonShow(false);
                setModalErrMessage('Missing designation id.');
                setModalResponse('error');
                return;
            }
            const pathParams: EditPathParams = {
                id: editIdParam,
            };
            handleApiCall(payload, pathParams, ErrorMessages.designationUpdation);
        } else {
            handleApiCall(payload, {}, ErrorMessages.designationCreation);
        }
    };

    return (
        <>
            <div className='organization page-container bg-customBg '>
                <div className=' full-width-container'>
                    <div className='organization_header py-5'>
                        {/* <h1 className='organization_title py-5 flex'>
                            <Link to='/home/dashboard'>
                                <span className='organization_back-arrow pr-4 hover:text-black'>
                                    <ArrowLeftOutlined />
                                </span>
                            </Link>
                            {orgName}
                            <Tooltip title='Edit Organization'>
                                <Link to='/home/organisation/edit-organisation'>
                                    <img src={editButton} alt='edit' className='pl-4 cursor-pointer' />
                                </Link>
                            </Tooltip>
                        </h1> */}
                    </div>

                    <div className='organization__Tab bg-white relative'>
                        <div className="organization-table">
                            <div className="bg-white p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <h2 className="text-lg font-semibold">
                                        {isViewOnly
                                            ? 'View Designation'
                                            : isEdit
                                                ? 'Edit Designation'
                                                : 'Create Designation'}
                                    </h2>
                                    <Link className='underline text-xs' target="_blank" to="/job-description">Sample Job Description</Link>
                                </div>

                                <form onSubmit={handleSubmit(onSubmit)}>
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" style={{ height: 'calc(100vh - 285px)' }}>
                                        {/* Left Column: Form Details & Roles */}
                                        <div className="flex flex-col gap-3 h-full">
                                            <div className='grid grid-cols-2 gap-4'>
                                                <div className='relative z-0 form-item font-rubik'>
                                                    <FloatLabel label='Department Name*' name='department' value={isEdit ? designationDepartmentList.filter((e) => e.value === editId) : createDepartment}>
                                                        <Select
                                                            className='float-control !pt-2'
                                                            showSearch
                                                            placeholder='Search Department*'
                                                            optionFilterProp='children'
                                                            onChange={item => onChange(item as number)}
                                                            filterOption={filterOption}
                                                            options={designationDepartmentList}
                                                            value={isEdit ? designationDepartmentList.filter((e) => e.value === editId) : createDepartment}
                                                            disabled={isViewOnly}
                                                        />
                                                    </FloatLabel>
                                                    {errors.department && validateDept && <ErrorMessage message={errors.department.message} />}
                                                </div>
                                                <div className='relative z-0 form-item font-rubik'>
                                                    <Controller
                                                        name='designation'
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Input
                                                                type='text'
                                                                id='email'
                                                                label='Designation*'
                                                                control={control}
                                                                {...field}
                                                                hasError={!!errors.designation}
                                                                disabled={isViewOnly}
                                                            />
                                                        )}
                                                    />
                                                    {errors.designation && <ErrorMessage message={errors.designation.message} />}
                                                </div>
                                                <div className='relative z-0 form-item font-rubik'>
                                                    <FloatLabel label='Grade *' name='grade' value={isEdit ? gradeList.filter((e) => e.value === editGrade) : createGrade}>
                                                        <Select
                                                            showSearch
                                                            placeholder='Select a grade'
                                                            className='float-control'
                                                            optionFilterProp='children'
                                                            onChange={e => gradeOnChange(e as number)}
                                                            filterOption={filterOption}
                                                            options={gradeList}
                                                            value={isEdit ? gradeList.filter((e) => e.value === editGrade) : createGrade}
                                                            disabled={isViewOnly}
                                                        />{' '}
                                                    </FloatLabel>
                                                    {errors.grade && validateGrade && <ErrorMessage message={errors.grade.message?.toString()} />}
                                                </div>

                                                <div className='relative z-0 form-item font-rubik'>
                                                    <Controller
                                                        name='experience'
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Input
                                                                type='text'
                                                                id='email'
                                                                label='Experience*'
                                                                control={control}
                                                                {...field}
                                                                hasError={!!errors.experience}
                                                                disabled={isViewOnly}
                                                            />
                                                        )}
                                                    />
                                                    {errors.experience && <ErrorMessage message={errors.experience.message} />}
                                                </div>
                                                <div className='relative z-0 form-item font-rubik col-span-2'>
                                                    <Controller
                                                        name='compensation'
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Input
                                                                type='text'
                                                                id='email'
                                                                label='Compensation*'
                                                                control={control}
                                                                {...field}
                                                                hasError={!!errors.compensation}
                                                                disabled={isViewOnly}
                                                            />
                                                        )}
                                                    />
                                                    {errors.compensation && <ErrorMessage message={errors.compensation.message} />}
                                                </div>
                                            </div>

                                            <div className="flex flex-col flex-1 min-h-0">
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Roles and Responsibilities</label>
                                                <div className="flex-1 relative border border-gray-300 rounded overflow-hidden">
                                                    <DesignationEditor
                                                        setValue={setValue}
                                                        editRecord={editRecord}
                                                        editModal={isEdit}
                                                        isToolBar={!isViewOnly}
                                                        readOnly={isViewOnly}
                                                    />
                                                </div>
                                                {errors.roles && <ErrorMessage message={errors.roles.message?.toString()} />}
                                            </div>
                                        </div>

                                        {/* Right Column: KPI Section */}
                                        <div className="h-full flex flex-col border-l pl-6 border-gray-200 overflow-hidden">
                                            <KPISection control={control} errors={errors} isReadOnly={isViewOnly} />
                                        </div>
                                    </div>

                                    <div className='mt-5 flex justify-end pt-3 border-t border-gray-100 items-center gap-3'>
                                        <button
                                            className='addButton secondary'
                                            type="button"
                                            onClick={handleClosePage}
                                            disabled={submitLoading}
                                        >
                                            {isViewOnly ? 'Close' : 'Cancel'}
                                        </button>
                                        {!isViewOnly && (
                                            <button
                                                className='addButton'
                                                type="submit"
                                                disabled={submitLoading}
                                            >
                                                {submitLoading ? (
                                                    <span className="flex items-center gap-2">
                                                        <span className="material-symbols-outlined animate-spin text-base">progress_activity</span>
                                                        {isEdit ? 'Update' : 'Create'}
                                                    </span>
                                                ) : (
                                                    isEdit ? 'Update' : 'Create'
                                                )}
                                            </button>
                                        )}
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>

                {popModal && <PopupModal message={modalerrMessage} open={popModal} handleModalClose={handleClosePopUpModal} response={modalResponse} modalButtonShow={modalButtonShow} />}
                {loader && <ModalLoader />}
            </div>
        </>
    );
};

export default CreateDesignationPage;
